import { Col, Row } from "../../../common/components/Layout";
import NullableImage from "../../../common/components/NullableImage";
import ProductDesignation from "../ProductDesignation";
import {
  AliasesParameter,
  OldDesignationParameter,
  SchaefflerIdParameter,
} from "../../../common/components/Product/ProductParams";
import { PredefinedTraits } from "../ProductTrait";
import { type PdpPageProductData } from "../../models/PdpPageProductData";
import { Permission, userHasPermission } from "../../../utils/permissions";
import { ImageFormat } from "../../../dto/ProductDataDTO";
import { NewVersionMessage } from "../../../common/components/Messages/NewVersionMessage";
import { isKITProduct, type PackagingInformationEntry } from "../../../models/ProductData";
import { HousingMessages } from "../../../common/components/Messages/HousingMessages";
import { getIsContractAvailable, isSpecialPrice } from "../../redux/productDetailsSelectors";
import { type AddToCartFormData, type OptionalPartFormData } from "../../../AddToCart/hooks/useAddToCartNew";
import { useFormContext } from "react-hook-form";

import { Trans, useTranslation } from "react-i18next";
import { getPrimaryImage } from "~/utils/product/getPrimaryImage";
import { useSelector } from "react-redux";
import { usePdpSummaryLogic } from "~/ProductDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { usePackageRestrictionContext } from "~/common/components/PackageRestrictionContext/PackageRestrictionContext";
import { canSeeListPrice } from "~/utils/features";
import { getListPrice, isProductPMA } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { type PriceData } from "~/models/PriceData";
import { multiply, volatileSum, zero } from "~/utils/PriceUtils";
import { type MouseEvent, useMemo } from "react";
import { AddToCartService } from "~/AddToCart/AddToCartService";
import { trackPdpClick, trackPdpWishlistClick } from "~/feature/Analytics/ga/ga";
import { userIsCustomer, userIsPublic, userIsRegistered } from "~/utils/roles";
import { getRedirectToRegisterPage, redirectToUrl } from "~/utils/url/urlUtils";
import { openAddToWishlistWizard } from "~/Wishlist/modal/AddToWishlist/AddToWishlistService";
import { mapOptionalParts4Wishlist } from "~/Wishlist/modal/AddToWishlist/utils";
import {
  ContactSalesTeamLink,
  PreferredProductsLink,
} from "~/ProductDetails/components/ProductSummary/common/PdpLinks";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import PdpPrice from "~/ProductDetails/components/PdpPrice";
import { PriceReferenceParam } from "~/feature/cart/cart/components/LineItemEditableParams";
import OptimeSolutionsMessage from "~/common/components/Messages/OptimeSolutionsMessage";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "~/utils/constants";
import { StockLevelSection } from "~/ProductDetails/components/AddToCartCard/StockLevelSection";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "~/ProductComparison/ProductComparisonCheckbox";
import { Card } from "~/Configurators/Linear/components/Card";
import { KitPrices } from "~/ProductDetails/components/ProductSummary/components/KitPrices";
import { Alert } from "~/common/m3/Alert/Alert";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { getPurchaseDataFromUrl, selectAmountInCart } from "./PdpSummaryHelper";
import { Button, Stack, styled, Typography } from "@mui/material";
import { usePurchasePlace } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import { renderButtons } from "~/ProductDetails/components/ProductSummary/components/Buttons/usePdpButtons";
import { TabId } from "~/ProductDetails/components/usePDPTabs";

import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import Cycle from "~/common/icons/Cycle";
import parse from "html-react-parser";
import { IncludesSurchargeDisclaimer } from "~/common/components/Messages/IncludesSurchargeDisclaimer";
import { isTariffSurchargeEnabled } from "~/feature/cart/cart/utils/cartUtils";
import ProductMMD from "~/feature/Product/elements/ProductMMD";
import MMDAssistant from "../MMDAssistant/MMDAssistant";
import { useDisplayMMDAssistant } from "~/ProductDetails/context/DisplayMMDAssistantContext";
import { PriceElements } from "~/common/components/Price/PriceV4";
import Tag from "~/common/components/Tag/Tag";
import { URL_RECONDITIONABLE } from "~/utils/url/staticUrls";
import { usePdpCmnLogic } from "../MMDAssistant/logic/usePdpCmnLogic";
import { Product } from "~/feature/Product";

interface Props {
  productDescriptor: ProductDescriptor<PdpPageProductData>;
  hasPreferredProducts: boolean;
  changeTab: (tabId: string) => void;
}

const isSalesman = userHasPermission(Permission.ADD_TO_QUOTE);

/*
 * Product Details page summary (starting from breadcrumbs, finishing before tabs) for all other cases.
 * Non-salesman, non-distributor - meaning it can be OEM purchaser, registered, anonymous (and others)
 */
export const DefaultPdpSummary = ({ productDescriptor, hasPreferredProducts, changeTab }: Props) => {
  const { t } = useTranslation();
  const reco_place = usePurchasePlace();
  const displayMMDAssistant = useDisplayMMDAssistant();

  const product = productDescriptor.base;

  const primaryImage = getPrimaryImage(product.images, { imageFormat: ImageFormat.product });

  const { manufacturerLogoUrl, brandLogoUrl } = product;

  const isContractProduct = getIsContractAvailable(productDescriptor);
  const isHousingKit = isKITProduct(product);

  const formProps = useFormContext<AddToCartFormData>();
  const quantity = formProps.watch("quantity");
  const packCode = formProps.watch("selectedPackCode");
  const priceReference = formProps.watch("priceReference");
  const pack: PackagingInformationEntry | null = formProps.watch("selectedPack");
  const price = formProps.watch("netPrice");

  const optionalParts = formProps.watch("optionalParts") as Dict<OptionalPartFormData>;

  const webServicesDisabled = window.SCHA.config.disableAllWS;
  const isError = productDescriptor.isError && !webServicesDisabled;

  const hasSurcharge = formProps.watch("surcharge") > 0;
  const tariffSurcharge = isTariffSurchargeEnabled() && formProps.watch("tariffSurcharge");

  const priceUnit = formProps.watch("priceUnit");

  const qtyInCart = useSelector(selectAmountInCart({ product: productDescriptor, packCode }));
  const {
    buttons: { showCartButton, showWishlistButton },
    flags: { showBetterDeal, showManufacturerLogo },
  } = usePdpSummaryLogic({
    product: productDescriptor,
    qtyInCart,
    quantity,
    packCode,
  });

  const restrictedPackage = usePackageRestrictionContext() ?? "";

  const isSpecial = isSpecialPrice(productDescriptor, "");

  const saleActive = false;
  const listPrice = getListPrice({ product: productDescriptor, packCode });
  const showListPrice =
    !saleActive &&
    !!listPrice &&
    canSeeListPrice({
      product: productDescriptor,
      isSpecialPrice: isSpecial,
      hasSurcharge: hasSurcharge,
    });

  const { isLoading, availability: availabilityInfo } = productDescriptor;
  const canSeePrice = userHasPermission(Permission.PRICE);

  const isPMA = isProductPMA(productDescriptor);

  const countTotal = (price: PriceData | null, priceUnit: number, qty: number) => {
    return !price ? null : multiply(price, qty / priceUnit);
  };

  const selectedOptionalParts = Object.values(optionalParts).filter((item) => item.selected);

  const optionalPartsPrice = volatileSum(
    ...selectedOptionalParts.map((item) => countTotal(item.netPrice, item.priceUnit, item.quantity) ?? zero()),
  );

  const optionalPartsHaveSurcharge =
    selectedOptionalParts.length > 0 && selectedOptionalParts.some((item) => item.surcharge > 0);

  const totalPrice = countTotal(price, priceUnit, quantity);

  const quantityVisible = product.quantityVisible && !product.externalProduct;
  const isExternalProduct = product.externalProduct;

  const { code: productCode, designation, isOptime, preferredProductsFamilyName } = productDescriptor.base;

  const priceInfo = productDescriptor.isMultiPrice ? null : productDescriptor.prices[0];
  const isPriceAt15Digit = productDescriptor.isMultiPrice;

  const { openAddToCartModal, checkAvailabilityClick, selectClick, openModalWishlist } = useMemo(() => {
    const openAddToCartModal = (e?: MouseEvent<HTMLElement>) => {
      if (e) {
        e.preventDefault();
      }
      const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();
      const context = {
        page: reco_place,
        recommendationPlace,
        recommendationType,
      };
      AddToCartService.openAddToCartWizard(
        {
          productCode,
          restrictionQuery: restrictedPackage,
          initialData: formProps.getValues(),
          source: "pdp",
        },
        context,
      );
    };
    return {
      openAddToCartModal,
      checkAvailabilityClick: () => {
        trackPdpClick("Check availability details", designation);
        openAddToCartModal();
      },
      selectClick: () => {
        trackPdpClick("Select Product", designation);
        openAddToCartModal();
      },
      openModalWishlist: (e?: MouseEvent<HTMLElement>) => {
        if (e) {
          e.preventDefault();
        }
        if (userIsPublic()) {
          redirectToUrl(getRedirectToRegisterPage({ openLogin: true }));
        } else {
          trackPdpWishlistClick(designation, 1);
          openAddToWishlistWizard({
            productCode,
            initialQuantity: quantity,
            initialPriceReference: priceReference,
            addTracking: (product) => trackPdpWishlistClick(product.base.designation, 2),
            selectedOptionalParts: mapOptionalParts4Wishlist(productDescriptor.base, optionalParts),
          });
        }
      },
    };
  }, [designation, formProps.getValues, productCode]);

  const qtyInputEnabled =
    !isLoading && ((showCartButton && !isError) || showWishlistButton) && productDescriptor.base.quantityVisible;

  const isContractItem = priceInfo ? priceInfo.isContractAvailable : false;

  let isAddToCartEnabled;
  if (isContractItem) {
    isAddToCartEnabled = userHasPermission(Permission.VIEW_CONTRACT) && userHasPermission(Permission.ADD_TO_CART);
  } else if (!isPriceAt15Digit) {
    isAddToCartEnabled = priceInfo && priceInfo.hasPrice && availabilityInfo && availabilityInfo.hasPackaging;
  } else {
    isAddToCartEnabled = availabilityInfo && availabilityInfo.hasPackaging;
  }

  let addToCartDisabled = !isAddToCartEnabled;

  if (isPMA || !userIsCustomer()) {
    addToCartDisabled = false;
  }
  if (webServicesDisabled || quantity < 1) {
    addToCartDisabled = true;
  }

  const isPublic = userIsPublic() || userIsRegistered();
  const showStockSection = !isPublic && !isHousingKit;

  const buttons = renderButtons(productDescriptor, {
    atc: {
      disabled: addToCartDisabled,
      onClick: selectClick,
    },
    wishlist: {
      onClick: openModalWishlist,
    },
    configurator: {
      onClick: () => changeTab(TabId.CONFIGURATOR),
    },
    atcFromSP: {
      quantity,
    },
  });

  const mmdCmnData = usePdpCmnLogic();

  return (
    <Row className={"scha__pdp__product-box"}>
      <Col size={4}>
        <Stack gap={2}>
          <Row>
            <Col size={9}>{primaryImage && <NullableImage data={primaryImage} />}</Col>
            <Col size={3} className="scha__product-image__logos">
              {showManufacturerLogo && (
                <img className={"scha__pdp__manufacturer-logo"} src={manufacturerLogoUrl} alt="manufacturer-logo" />
              )}
              {brandLogoUrl && <img className={"scha__pdp__brand-logo"} src={brandLogoUrl} alt="brand-logo" />}
            </Col>
          </Row>
          {isHousingKit && <HousingMessages.NotFinal />}
        </Stack>
      </Col>

      <Col size={8}>
        <Stack gap={2}>
          {displayMMDAssistant && (
            <MMDAssistant
              marketDesignation={product.marketDesignation}
              initialData={{
                quantity: quantity,
                priceReference: priceReference,
                ...mmdCmnData,
              }}
            />
          )}

          <Stack gap={2} direction={"row"}>
            <Col size={6} className={"scha__pdp__baseinfo-col"}>
              <Stack gap={2}>
                <ProductDesignation
                  as={"h1"}
                  designation={product.designation}
                  size={"headline-medium"}
                  isPreferred={product.preferred}
                  showCopyClipboard
                />

                {product.marketDesignation && <ProductMMD marketDesignation={product.marketDesignation} />}

                <Stack gap={1}>
                  <Product.ShortDescriptionV2
                    component={"h2"}
                    variant={"titleMedium"}
                    data-test-id={"pdp-short-description"}
                  />
                  <Product.DescriptionV2
                    component={"h3"}
                    variant={"titleSmall"}
                    data-test-id={"pdp-long-description"}
                  />
                </Stack>
                {productDescriptor.base.reconditionable && (
                  <RichTooltip
                    content={
                      <Typography>
                        {parse(t("cmsnavigationnode.schaefflerContentCatalog.reconditionableInfo"))}
                      </Typography>
                    }
                    actions={[
                      <Button href={URL_RECONDITIONABLE} variant="text">
                        {t("cmsnavigationnode.schaefflerContentCatalog.moreInfo")}
                      </Button>,
                    ]}
                  >
                    <Tag icon={<Cycle fontSize="small" />} iconPosition="start" variant="success">
                      {t("cmsnavigationnode.schaefflerContentCatalog.reconditionable")}
                    </Tag>
                  </RichTooltip>
                )}

                <NewVersionMessage baseProduct={product} />

                <Stack gap={2}>
                  <OldDesignationParameter product={product} label={t("productDetailPage.oldDesignation")} />
                  <SchaefflerIdParameter productDescriptor={productDescriptor} />
                  <AliasesParameter product={product} mode={isSalesman ? "13-digits" : "all"} />
                </Stack>

                <Stack gap={1}>
                  {product.preferred && <PredefinedTraits.PreferredProduct />}
                  {isContractProduct && <PredefinedTraits.ContractProduct />}
                  {/*<PredefinedTraits.BoughtBefore/>*/}
                </Stack>
              </Stack>
            </Col>

            <Col size={6} className={"scha__pdp__purchase-card"}>
              <StyledCard disableHover={true}>
                <Stack gap={2}>
                  {isError && t("hybrisAjaxError")}

                  {isLoading && <CircleSpinner />}

                  {!isLoading && (
                    <>
                      {canSeePrice && (
                        <>
                          {isPMA ? (
                            <InfoProductMessagePMA />
                          ) : isHousingKit && totalPrice ? (
                            <KitPrices
                              className={"mb-40"}
                              product={productDescriptor.base}
                              totalPrice={totalPrice}
                              optionalPartsPrice={optionalPartsPrice}
                              includesSurcharge={hasSurcharge}
                              optionalPartsIncludesSurcharge={optionalPartsHaveSurcharge}
                            />
                          ) : (
                            <PdpPrice
                              price={price}
                              isMultiPrice={isPriceAt15Digit}
                              priceUnit={priceUnit}
                              isMonthlySubsription={!quantityVisible}
                              isSpecialPrice={isSpecial}
                              includesSurcharge={hasSurcharge}
                              tariffSurcharge={tariffSurcharge}
                              product={productDescriptor}
                              quantity={quantity}
                            />
                          )}
                        </>
                      )}
                      {showListPrice && (
                        <PriceElements.Stack>
                          <PriceElements.Price price={listPrice} variant="titleMedium" />
                          <PriceElements.ListPriceLabel unit={priceUnit} variant="labelLarge" color={"textSecondary"} />
                        </PriceElements.Stack>
                      )}
                      {isPriceAt15Digit && (
                        <Alert severity={"info"}>
                          <Trans i18nKey={"productdetail.multipleprices.purchaser"}>
                            Multiple prices possible, please
                            <a href={"#"} onClick={openAddToCartModal}>
                              select package option
                            </a>
                          </Trans>
                        </Alert>
                      )}
                    </>
                  )}

                  {!isExternalProduct && (
                    <PriceReferenceParam
                      data-test-id={"pdp-price-reference"}
                      priceReference={priceReference}
                      onChange={(value) => formProps.setValue("priceReference", value ?? "")}
                      product={productDescriptor.base}
                      editable={qtyInputEnabled}
                    />
                  )}

                  {isOptime && <OptimeSolutionsMessage />}

                  {quantityVisible && (
                    <QuantityInput
                      min={1}
                      data-test-id={"pdp-input-quantity"}
                      max={MAX_QUANTITY}
                      value={quantity}
                      variant={"compact"}
                      disabled={!qtyInputEnabled}
                      step={1}
                      onValueChange={(newVal) =>
                        formProps.setValue("quantity", newVal, { shouldTouch: true, shouldDirty: true })
                      }
                    />
                  )}

                  {showBetterDeal && <Alert severity="info">{t("addProduct.scalingDeal")}</Alert>}

                  {showStockSection && (
                    <StockLevelSection
                      productDescriptor={productDescriptor}
                      quantity={quantity}
                      onCheckAvailabilityClick={checkAvailabilityClick}
                      checkAvailabilityDisabled={showCartButton ? addToCartDisabled : false}
                    />
                  )}

                  {hasPreferredProducts && <PreferredProductsLink baseType={preferredProductsFamilyName} />}
                  {!webServicesDisabled && showCartButton && canSeePrice && productDescriptor.isError && (
                    <Alert severity="error">{t("addProduct.wsError")}</Alert>
                  )}

                  {isProductComparisonCheckboxVisible(productDescriptor.base) && !isExternalProduct && (
                    <ProductComparisonCheckbox productCode={productCode} gaLocation={"Product Detail Page"} />
                  )}

                  <Stack gap={1}>{buttons}</Stack>
                  <ContactSalesTeamLink product={product} />
                  {!tariffSurcharge && (
                    <IncludesSurchargeDisclaimer hasSurcharge={hasSurcharge} tariffSurcharge={false} />
                  )}
                </Stack>
              </StyledCard>
            </Col>
          </Stack>
        </Stack>
      </Col>
    </Row>
  );
};

const StyledCard = styled(Card)({
  padding: "16px",
});
